import { curveMonotoneX } from '@visx/curve'
import { scaleLinear } from '@visx/scale'
import { AreaClosed, LinePath } from '@visx/shape'
import { memo, useMemo } from 'react'

import { useElementSize } from './useElementSize.ts'

interface SparklineProps {
  values: readonly number[]
  height?: number
  /** Accessible summary — a sparkline with no label is decoration. */
  label: string
  /** Forces the y-domain floor to zero. Off for values that hover in a band. */
  zeroBased?: boolean
}

/**
 * A trend, not a reading.
 *
 * No axes, no ticks, no hover: the exact numbers are always printed next to it.
 * This exists to answer "climbing, flat or sawtooth?" in peripheral vision.
 */
export const Sparkline = memo(function Sparkline({
  values,
  height = 26,
  label,
  zeroBased = false,
}: SparklineProps) {
  const [ref, size] = useElementSize<HTMLDivElement>()
  const width = size.width

  const { xScale, yScale } = useMemo(() => {
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    for (const value of values) {
      if (value < min) min = value
      if (value > max) max = value
    }
    if (!Number.isFinite(min)) {
      min = 0
      max = 1
    }
    const floor = zeroBased ? 0 : min - (max - min) * 0.15
    const ceiling = max + (max - min) * 0.15 || max + 1

    return {
      xScale: scaleLinear<number>({
        domain: [0, Math.max(1, values.length - 1)],
        range: [0, width],
      }),
      yScale: scaleLinear<number>({
        domain: [floor, ceiling],
        range: [height - 1, 1],
      }),
    }
  }, [values, width, height, zeroBased])

  return (
    <div ref={ref} className="live-chart w-full" style={{ height }}>
      {width > 0 && values.length > 1 && (
        <svg width={width} height={height} role="img" aria-label={label}>
          <AreaClosed<number>
            data={values as number[]}
            x={(_, index) => xScale(index)}
            y={(value) => yScale(value)}
            yScale={yScale}
            curve={curveMonotoneX}
            fill="var(--c-series-fill)"
          />
          <LinePath<number>
            data={values as number[]}
            x={(_, index) => xScale(index)}
            y={(value) => yScale(value)}
            curve={curveMonotoneX}
            stroke="var(--c-series-2)"
            strokeWidth={1.25}
          />
        </svg>
      )}
    </div>
  )
})
